import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Stack,
    Typography,
    IconButton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { buildBirthDateSchema } from '../../utils/familyAgeValidation.js';
import FormProvider, {
    RHFTextField,
    RHFUploadAvatar,
    RHFPhoneInput,
} from '../../components/minimal/hook-form/index.jsx';
import RHFBirthdayPicker from '../../components/minimal/hook-form/RHFBirthdayPicker.jsx';
import { useLocales } from '../../locales';
import { useSnackbar } from '../../components/minimal/snackbar/index.jsx';
import { useSettingsContext } from '../../components/minimal/settings/SettingsContext';
import * as Sentry from '@sentry/react';
import * as API from '../../api';
import { S3Handler } from '../../utils/s3.js';
import { getApiErrorMessage } from '../../utils/errorUtils.js';
import { fData } from '../../utils/minimal/formatNumber.js';
import Iconify from '../../components/minimal/iconify';
import AvatarPositionEditor from '../../components/minimal/upload/AvatarPositionEditor';

const CompleteFamilyProfileDialog = ({
    open,
    onClose,
    onSuccess,
    member,
    handler,
    token,
    subscriptionTemplate,
    familyChildMaxAge,
    account,
    familyRequiredFields,
}) => {
    const { translate } = useLocales();
    const { enqueueSnackbar } = useSnackbar();
    const settings = useSettingsContext();
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);

    const isChild = member?.familyRole === 'child';
    // Prefer the value returned by the getFamilyMembers API (reliable on this
    // route); fall back to the subscription template from context if present.
    const maxAge = familyChildMaxAge ?? subscriptionTemplate?.familyChildMaxAge;
    // Determine required fields from server config, with sensible defaults
    const roleKey = isChild ? 'child' : 'adult';
    const requiredFields = familyRequiredFields?.[roleKey] || (isChild ? ['fname', 'lname', 'birth_date'] : ['fname', 'lname', 'phone', 'birth_date']);
    const isFieldRequired = (field) => requiredFields.includes(field);

    const schema = Yup.object().shape({
        fname: isFieldRequired('fname')
            ? Yup.string().trim().required(translate('dashboard.events.required_field'))
            : Yup.string().trim(),
        lname: isFieldRequired('lname')
            ? Yup.string().trim().required(translate('dashboard.events.required_field'))
            : Yup.string().trim(),
        birth_date: buildBirthDateSchema({
            isChild,
            maxAge,
            required: isFieldRequired('birth_date'),
            translate,
        }),
        phone: isFieldRequired('phone')
            ? Yup.string().trim().required(translate('dashboard.events.required_field'))
            : Yup.string().trim(),
        idOrPassport: isFieldRequired('idOrPassport')
            ? Yup.string().trim().required(translate('dashboard.events.required_field'))
            : Yup.string().trim(),
        social_link: isFieldRequired('social_link')
            ? Yup.string().trim().required(translate('dashboard.events.required_field'))
            : Yup.string().trim(),
        imageUrl: Yup.mixed().nullable(),
    });

    const existingEndUser = member?.endUser || {};
    // No lock-once-filled here, deliberately (#1512). This dialog only opens while the profile is
    // INCOMPLETE -- FamilyMemberCard offers "Complete" only then, and the backend refuses a
    // complete row with `profile_already_complete`. So a parent correcting a detail they typed
    // themselves is the only thing a lock could block, and once the member is complete their
    // details are already unreachable from the member side.

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            fname: existingEndUser.fname || '',
            lname: existingEndUser.lname || '',
            birth_date: existingEndUser.birth_date || null,
            phone: existingEndUser.phone || '',
            idOrPassport: existingEndUser.idOrPassport || '',
            social_link: existingEndUser.social_link || '',
            imageUrl: existingEndUser.imageUrl || null,
        },
    });

    const { handleSubmit, setValue, watch, reset } = methods;

    useEffect(() => {
        if (open && member) {
            const eu = member?.endUser || {};
            // Parse memberName into fname/lname if endUser data is not available
            const nameParts = member?.memberName?.split(' ') || [];
            const fallbackFname = nameParts[0] || '';
            const fallbackLname = nameParts.slice(1).join(' ') || '';
            reset({
                fname: eu.fname || fallbackFname,
                lname: eu.lname || fallbackLname,
                birth_date: eu.birth_date || member?.birth_date || null,
                phone: eu.phone || member?.phone || '',
                idOrPassport: eu.idOrPassport || member?.idOrPassport || '',
                social_link: eu.social_link || '',
                imageUrl: eu.imageUrl || member?.imageUrl || null,
            });
        }
    }, [open, member, reset]);

    const handleDrop = (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (!file) return;
        setPendingFile(file);
    };

    const uploadFramed = async (framedFile) => {
        // Revoke a prior framed blob URL when replacing it (avoids leaking on repeated
        // uploads). We never revoke the URL currently shown — only the one being replaced.
        const prev = methods.getValues('imageUrl');
        if (prev && typeof prev === 'object' && prev.preview && prev.preview !== framedFile.preview) {
            URL.revokeObjectURL(prev.preview);
        }
        framedFile.preview = framedFile.preview || URL.createObjectURL(framedFile);
        setValue('imageUrl', framedFile, { shouldValidate: true, shouldDirty: true });
        setIsUploadingImage(true);
        try {
            framedFile.finalPath = await S3Handler.uploadFile(framedFile, null, null, null, true, token);
            setValue('imageUrl', framedFile, { shouldValidate: true, shouldDirty: true });
        } catch (e) {
            // The S3 PUT went through un-instrumented axios, so this error was previously invisible
            // to Sentry (#877). Report it with enough context to diagnose per-user upload failures.
            Sentry.captureException(e, {
                extra: {
                    context: 'CompleteFamilyProfileDialog.uploadFramed',
                    memberId: member?._id,
                    sellerId: account?._id,
                    marketplaceId: account?.marketPlace?._id,
                    subscriptionTemplateId: subscriptionTemplate?._id,
                    handler,
                    fileName: framedFile?.name,
                    fileSize: framedFile?.size,
                    fileType: framedFile?.type,
                },
            });
            enqueueSnackbar(getApiErrorMessage(e, translate), { variant: 'error' });
        } finally {
            setIsUploadingImage(false);
            setPendingFile(null);
        }
    };

    const onSubmit = async (data) => {
        // A photo was chosen but its S3 upload never completed (object without finalPath) — e.g.
        // the upload failed. Previously Save proceeded and dropped the image, leaving the profile
        // stuck at isProfileComplete:false forever (#877). Block Save with an actionable message.
        const chosenImage = data.imageUrl;
        if (chosenImage && typeof chosenImage === 'object' && !chosenImage.finalPath) {
            enqueueSnackbar(translate('image_upload.upload_failed_retry'), { variant: 'error' });
            return;
        }
        try {
            setIsSubmitting(true);

            const payload = {
                fname: data.fname,
                lname: data.lname,
                birth_date: data.birth_date || undefined,
                ...(isFieldRequired('phone') ? { phone: data.phone } : {}),
                ...(isFieldRequired('idOrPassport') ? { idOrPassport: data.idOrPassport } : {}),
                ...(isFieldRequired('social_link') ? { social_link: data.social_link } : {}),
            };

            if (data.imageUrl?.finalPath) {
                payload.imageUrl = data.imageUrl.finalPath;
            } else if (typeof data.imageUrl === 'string' && data.imageUrl) {
                payload.imageUrl = data.imageUrl;
            }

            const marketplaceId = account?.marketPlace?._id;
            const sellerId = account?._id;
            await API.completeFamilyProfile({ marketplaceId, sellerId, id: member._id, token, data: payload });
            enqueueSnackbar(translate('snackbar.details_updated_successfully'));
            onSuccess && onSuccess();
            reset();
        } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, translate), { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        reset();
        onClose && onClose();
    };

    const imageValue = watch('imageUrl');

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="h6">
                            {member?.isProfileComplete
                                ? translate('family.edit_member')
                                : translate('family.complete_profile')}
                        </Typography>
                        <Chip
                            label={isChild
                                ? translate('family.child')
                                : member?.familyRole === 'parent'
                                    ? translate('family.parent')
                                    : translate('family.adult')}
                            color={isChild ? 'warning' : member?.familyRole === 'parent' ? 'primary' : 'info'}
                            size="small"
                            variant="soft"
                        />
                    </Stack>
                    <IconButton onClick={handleClose}>
                        <Iconify width="24px" icon="eva:close-outline" />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <FormProvider methods={methods}>
                    <Stack spacing={3} sx={{ pt: 1 }}>
                        {/* Photo upload */}
                        <Stack alignItems="center" spacing={1}>
                            <RHFUploadAvatar
                                name="imageUrl"
                                maxSize={10485760}
                                onDrop={handleDrop}
                                disabled={isUploadingImage}
                                helperText={
                                    <Stack alignItems="center" spacing={0.5}>
                                        <Typography
                                            variant="caption"
                                            sx={{ display: 'block', textAlign: 'center', color: 'text.secondary' }}
                                        >
                                            {translate('image_upload.allowed_formats')}
                                            <br />
                                            {translate('image_upload.max_size', { size: fData(10485760) })}
                                        </Typography>
                                        <Typography variant="caption" color="warning.main">
                                            {translate('family.photo_required')}
                                        </Typography>
                                    </Stack>
                                }
                            />
                        </Stack>

                        <AvatarPositionEditor
                            open={!!pendingFile}
                            file={pendingFile}
                            onCancel={() => setPendingFile(null)}
                            onConfirm={uploadFramed}
                        />

                        {/* Name fields */}
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                        >
                            <RHFTextField
                                name="fname"
                                label={translate('family.first_name')}
                                fullWidth
                                required={isFieldRequired('fname')}
                            />
                            <RHFTextField
                                name="lname"
                                label={translate('family.last_name')}
                                fullWidth
                                required={isFieldRequired('lname')}
                            />
                        </Stack>

                        {/* Date of birth */}
                        <RHFBirthdayPicker
                            name="birth_date"
                            label={translate('family.date_of_birth')}
                            required={isFieldRequired('birth_date')}
                        />

                        {/* ID / Passport — only shown if in required fields for this role */}
                        {isFieldRequired('idOrPassport') && (
                            <RHFTextField
                                name="idOrPassport"
                                label={translate('family.id_or_passport')}
                                fullWidth
                                required
                            />
                        )}

                        {/* Social Link — only shown if in required fields for this role */}
                        {isFieldRequired('social_link') && (
                            <RHFTextField
                                name="social_link"
                                label={translate('family.social_link')}
                                fullWidth
                                required
                            />
                        )}

                        {/* Phone - only for adults */}
                        {/* Phone — only shown if in required fields for this role */}
                        {isFieldRequired('phone') && (
                            <RHFPhoneInput
                                name="phone"
                                label={translate('phone')}
                                required
                                forceCallingCode={true}
                                sx={{ direction: settings?.themeDirection }}
                            />
                        )}
                    </Stack>
                </FormProvider>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button variant="outlined" onClick={handleClose}>
                    {translate('cancel')}
                </Button>
                <LoadingButton
                    variant="contained"
                    loading={isSubmitting || isUploadingImage}
                    onClick={handleSubmit(onSubmit)}
                >
                    {translate('save')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default CompleteFamilyProfileDialog;
